import type { LocaleId } from '../domain/types.js';
import { bindLocaleSelector, detectLocaleFromEnvironment, localizeDocument } from './i18n.js';
import { getTrackingPayload, isTrackableElement } from './metrics.js';

const revealClass = 'is-visible';

export const createRevealObserver = (root: Document): IntersectionObserver | null => {
  const elements = [...root.querySelectorAll<HTMLElement>('.section, [data-reveal-group] > *')];
  elements.forEach((element, index) => {
    element.style.setProperty('--reveal-index', String(index % 4));
  });
  const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

  if (prefersReducedMotion || !('IntersectionObserver' in window)) {
    elements.forEach((element) => element.classList.add(revealClass));
    return null;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add(revealClass);
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.12 }
  );

  elements.forEach((element) => observer.observe(element));
  return observer;
};

export const bindAnalytics = (root: Document, logger: (eventName: string, payload: object) => void): void => {
  root.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-analytics-id]') : null;
    if (isTrackableElement(target)) {
      logger('landing_page_cta_click', getTrackingPayload(target));
    }
  });
};

export const dispatchAnalyticsEvent = (eventName: string, payload: object): void => {
  window.dispatchEvent(new CustomEvent(eventName, { detail: payload }));
};

export const initializeLandingPage = (
  root: Document,
  logger: (eventName: string, payload: object) => void = dispatchAnalyticsEvent
): Promise<void> => {
  createRevealObserver(root);
  bindAnalytics(root, logger);

  const setLocale = async (locale: LocaleId): Promise<void> => {
    await localizeDocument(root, locale);
  };

  const initialLocale = detectLocaleFromEnvironment();
  bindLocaleSelector(root, (locale) => {
    void setLocale(locale);
  });

  return setLocale(initialLocale);
};

/* v8 ignore next 3 -- exercised by browsers, not Node test runtime */
if (typeof document !== 'undefined') {
  document.documentElement.classList.add('has-js');
  initializeLandingPage(document);
}
